import { useCallback, useEffect, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';

import { type MentionProvider } from '@atlaskit/mention/resource';
import type {
	MentionAgent,
	MentionAgentSource,
} from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import { SmartMentionResource, type SmartMentionConfig } from '@atlassian/smart-mention-resource';

import { createRovoChatMentionResource } from '../services/rovo-chat-mention-resource/create-rovo-chat-mention-resource';
import {
	type MentionAnalyticsCallback,
	type MentionAnalyticsEvent,
} from '../services/rovo-chat-mention-resource/mention-analytics-event';
import {
	RovoChatMentionResource,
	type SelectedAgentRef,
} from '../services/rovo-chat-mention-resource/rovo-chat-mention-resource';

/** Active users only — no Connect bots, no apps. Agents come from the
 *  Relay-backed `MentionAgentSource`, not URS. */
const ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER =
	'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app)';
const AGENT_SEARCH_DEBOUNCE_MS = 300;

type UseChatMentionResourceOptions = {
	productKey: string;
	siteId: string;
	orgId: string | undefined;
	currentUserId: string | undefined;
	/**
	 * Optional consumer-provided mention provider. Three cases are
	 * recognised by the resource-selection logic:
	 *
	 *   1. `SmartMentionResource` instance → wrapped in a fresh
	 *      `RovoChatMentionResource` so the host's people search keeps
	 *      working AND Rovo agents are folded in. The host retains
	 *      ownership of the SMR lifecycle (we do NOT mark it as owned).
	 *   2. Any other `MentionProvider` (test harness, embed surface
	 *      with custom logic) → passed through unchanged; the picker is
	 *      people-only because we don't have a SMR to wrap.
	 *   3. `undefined` → we build URS internally via
	 *      `createRovoChatMentionResource`, which takes ownership.
	 */
	mentionResourceOverride: MentionProvider | undefined;
	/**
	 * When `true`, the resource pulls agent rows from `agentSource` and
	 * folds them under people in the typeahead. Off by default.
	 */
	enableAgents: boolean;
	/**
	 * The agent source from `useMentionAgentSource()`. `null` /
	 * `undefined` is tolerated and produces a people-only picker.
	 * Required only when `enableAgents` is `true`; safe to omit when
	 * agents are disabled (e.g. inline-chat-prompt surfaces).
	 */
	agentSource?: MentionAgentSource | null | undefined;
	/**
	 * Fired when the user picks an agent row. Function-form so the
	 * handler can be swapped without rebuilding the resource.
	 * Required only when `enableAgents` is `true`.
	 */
	onAgentSelected?: (agent: SelectedAgentRef) => void;
	/**
	 * Getter for the conversation's currently-active agent ids. Read
	 * lazily so the predicate sees the freshest store state without
	 * rebuilding the resource on every selection change. When omitted,
	 * chips are never disabled.
	 */
	getSelectedAgentIds?: () => readonly string[];
	getAgentMentionNodeId?: (agent: MentionAgent) => string | null;
	/**
	 * Translated tooltip rendered on disabled (non-active) agent chips.
	 * Omit to make the disabled-state predicate a no-op (graceful
	 * degradation for surfaces that haven't wired translations).
	 */
	disabledAgentTooltip?: string;
	/**
	 * Fires when an agent-mention chip is destroyed in the editor.
	 * Forwarded only for ids in `getSelectedAgentIds()`.
	 */
	onAgentMentionDestroyed?: (agentId: string) => void;
	/**
	 * Receives people, team, and agent mention-picker events. Its presence
	 * enables the analytics-capable resource even when agent rows are hidden.
	 */
	onMentionAnalyticsEvent?: MentionAnalyticsCallback;
	/**
	 * True when the conversation is a dedicated (non-default) agent chat
	 */
	isDedicatedAgentChat?: boolean;
	/**
	 * i18n label for the synthetic "Rovo" mention row. Resolved via `formatMessage` by the
	 * caller since the resource is a plain class. The row only appears when this is provided.
	 */
	rovoMentionLabel?: string;
};

/** Owns the lifecycle of the @-mention provider for Rovo Chat. Disposes
 *  wrapped resources on replacement / unmount; bare `MentionProvider`
 *  overrides are passed through untouched. */
export const useChatMentionResource = ({
	productKey,
	siteId,
	orgId,
	currentUserId,
	mentionResourceOverride,
	enableAgents,
	agentSource,
	onAgentSelected,
	getSelectedAgentIds,
	getAgentMentionNodeId,
	disabledAgentTooltip,
	onAgentMentionDestroyed,
	onMentionAnalyticsEvent,
	isDedicatedAgentChat,
	rovoMentionLabel,
}: UseChatMentionResourceOptions): Promise<MentionProvider> => {
	di(SmartMentionResource);

	const onAgentSelectedRef = useRef(onAgentSelected);
	onAgentSelectedRef.current = onAgentSelected;
	const getSelectedAgentIdsRef = useRef(getSelectedAgentIds);
	getSelectedAgentIdsRef.current = getSelectedAgentIds;
	const onAgentMentionDestroyedRef = useRef(onAgentMentionDestroyed);
	onAgentMentionDestroyedRef.current = onAgentMentionDestroyed;
	const disabledAgentTooltipRef = useRef(disabledAgentTooltip);
	disabledAgentTooltipRef.current = disabledAgentTooltip;
	const onMentionAnalyticsEventRef = useRef(onMentionAnalyticsEvent);
	onMentionAnalyticsEventRef.current = onMentionAnalyticsEvent;

	const handleAgentSelected = useCallback((agent: SelectedAgentRef) => {
		onAgentSelectedRef.current?.(agent);
	}, []);
	const getCurrentSelectedAgentIds = useCallback(
		() => getSelectedAgentIdsRef.current?.() ?? [],
		[],
	);
	const handleAgentMentionDestroyed = useCallback((agentId: string) => {
		onAgentMentionDestroyedRef.current?.(agentId);
	}, []);
	const handleMentionAnalyticsEvent = useCallback((event: MentionAnalyticsEvent) => {
		onMentionAnalyticsEventRef.current?.(event);
	}, []);
	const hasMentionAnalyticsCallback = onMentionAnalyticsEvent !== undefined;
	const shouldUseRovoChatMentionResource = enableAgents || hasMentionAnalyticsCallback;

	const provider = useMemo<MentionProvider>(() => {
		const agentMentionResourceOptions = enableAgents
			? {
					agentSource: agentSource ?? undefined,
					debounceTime: AGENT_SEARCH_DEBOUNCE_MS,
					onAgentSelected: handleAgentSelected,
					getSelectedAgentIds: getCurrentSelectedAgentIds,
					getAgentMentionNodeId,
					disabledAgentTooltip: disabledAgentTooltipRef.current,
					onAgentMentionDestroyed: handleAgentMentionDestroyed,
					isDedicatedAgentChat,
					rovoMentionLabel,
				}
			: {};

		// CASE 1: host handed us a SmartMentionResource. Keep it untouched
		// unless agent composition or mention analytics requires the Rovo Chat
		// resource. Analytics-only mode forwards the native people result set
		// unchanged.
		if (mentionResourceOverride instanceof SmartMentionResource) {
			if (!shouldUseRovoChatMentionResource) {
				return mentionResourceOverride;
			}
			return new RovoChatMentionResource({
				peopleResource: mentionResourceOverride,
				...agentMentionResourceOptions,
			});
		}

		// CASE 2: any other provider override → pass through unchanged.
		// People-only because we cannot wrap a non-SMR with agent
		// support (the agent-merge layer needs SMR's subscribe API).
		if (mentionResourceOverride) {
			return mentionResourceOverride;
		}

		// CASE 3: default — build URS internally. Keep the native resource when
		// neither agent composition nor mention analytics is needed. Otherwise,
		// use the Rovo Chat resource; its people-only path is transparent to the
		// native Smart Mention result set.
		const peopleConfig: SmartMentionConfig = {
			productKey,
			siteId,
			includeTeams: true,
			includeUsers: true,
			orgId,
			searchQueryFilter: ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER,
			productAttributes: { client: 'rovo-chat' },
			shouldHighlightMention: (mention) => mention.id === currentUserId,
		};

		if (!shouldUseRovoChatMentionResource) {
			return new SmartMentionResource(peopleConfig);
		}

		return createRovoChatMentionResource({
			peopleConfig,
			...agentMentionResourceOptions,
		});
	}, [
		productKey,
		siteId,
		orgId,
		currentUserId,
		mentionResourceOverride,
		enableAgents,
		agentSource,
		handleAgentSelected,
		getCurrentSelectedAgentIds,
		getAgentMentionNodeId,
		handleAgentMentionDestroyed,
		isDedicatedAgentChat,
		rovoMentionLabel,
		shouldUseRovoChatMentionResource,
	]);

	useEffect(() => {
		if (!(provider instanceof RovoChatMentionResource)) {
			return;
		}

		provider.setMentionAnalyticsCallback(
			hasMentionAnalyticsCallback ? handleMentionAnalyticsEvent : undefined,
		);
		return () => {
			provider.setMentionAnalyticsCallback(undefined);
		};
	}, [provider, hasMentionAnalyticsCallback, handleMentionAnalyticsEvent]);

	useEffect(() => {
		if (provider instanceof RovoChatMentionResource) {
			provider.setDisabledAgentTooltip(disabledAgentTooltip);
		}
	}, [disabledAgentTooltip, provider]);
	useEffect(() => {
		if (!(provider instanceof RovoChatMentionResource)) {
			return;
		}
		return () => provider.dispose();
	}, [provider]);

	return useMemo(() => Promise.resolve(provider), [provider]);
};
